import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api, clientId } from "@/app/api/client"

// Remembered assignee (Api::V1::PartiesController). Written server-side as a
// side effect of generating an assignment.
export interface Party {
  id: string
  name: string
  address: string | null
}

// The client's own block printed on assignments (clients.settings["assignor"]).
// legal_name falls back to the client's display name server-side.
export interface AssignorSettings {
  legal_name: string
  address: string | null
  signer: string | null
  signer_title: string | null
}

export type AssignmentInterest = "arti" | "pct" | "netacres"

export interface AssignmentRequest {
  lease_ids: string[]
  assignee_name: string
  assignee_address: string
  effective_on: string | null
  interest: AssignmentInterest
  pct?: string
  net_acres?: string
  per_str?: Record<string, string>
  nri_pct?: string
  signer: string
  signer_title: string
}

// One recordable instrument per county; `html` is MSO-HTML that Word opens
// as a .doc.
export interface AssignmentDocument {
  county: string
  filename: string
  html: string
  lease_count: number
  has_exhibit_b: boolean
}

export function useParties(opts: { enabled?: boolean } = {}) {
  return useQuery<Party[]>({
    queryKey: ["parties", clientId],
    queryFn: () => api.get<Party[]>("/parties"),
    enabled: opts.enabled,
  })
}

export function useAssignorSettings(opts: { enabled?: boolean } = {}) {
  return useQuery<AssignorSettings>({
    queryKey: ["assignor_settings", clientId],
    queryFn: () => api.get<AssignorSettings>("/assignor_settings"),
    enabled: opts.enabled,
  })
}

export function useUpdateAssignorSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<AssignorSettings>) =>
      api.patch<AssignorSettings>("/assignor_settings", payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assignor_settings"] }),
  })
}

export function useGenerateAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AssignmentRequest) =>
      api.post<{ documents: AssignmentDocument[] }>("/assignment_docs", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parties"] })
      queryClient.invalidateQueries({ queryKey: ["assignor_settings"] })
    },
  })
}

export function downloadAssignmentDocument(doc: AssignmentDocument) {
  const url = URL.createObjectURL(new Blob([doc.html], { type: "application/msword" }))
  const a = document.createElement("a")
  a.href = url
  a.download = doc.filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
